/**
 * Consumer funnel chrome — canonical Cardmoji design system (screens 2–10).
 *
 * One responsive implementation per screen: base styles reproduce the mobile
 * design files, `md:` and up reproduces the `*w` web design files (wider
 * container, clamped display type). The rounded "device" frame in the design
 * files is preview chrome only and is intentionally not reproduced.
 *
 * LEGAL GATES (see mem://legal/landing-copy-gates, docs/SECURITY-INVARIANTS.md L1):
 *  - No card scheme name or scheme mark anywhere in this surface.
 *  - No patent claim without an exact granted number.
 */
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { recordSteppedBack } from "@/lib/funnel-signals";

/* ------------------------------------------------------------------ layout */

export function FunnelPage({
  children,
  tone = "paper",
  pb = "pb-[150px]",
  className,
  testId,
}: {
  children: ReactNode;
  tone?: "paper" | "ink";
  pb?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "relative min-h-[100dvh] font-brand-body antialiased",
        tone === "ink" ? "bg-ink text-ink-foreground" : "bg-paper text-paper-foreground",
        pb,
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Content column. `size` maps to the max-width used by each web design file. */
export function FunnelContainer({
  children,
  size = "form",
  className,
}: {
  children: ReactNode;
  size?: "form" | "wide" | "account" | "creator";
  className?: string;
}) {
  const max = {
    form: "md:max-w-[720px]",
    wide: "md:max-w-[1180px]",
    account: "md:max-w-[980px]",
    creator: "md:max-w-[1080px]",
  }[size];
  return (
    <div className={cn("mx-auto w-full px-4 pt-[18px] md:px-8 md:pt-7", max, className)}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ header */

export function BackChevron({ to, label, onClick }: { to?: string; label: string; onClick?: () => void }) {
  const inner = (
    <svg viewBox="0 0 24 24" className="h-5 w-5">
      <path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const cls =
    "-ml-3 grid h-11 w-11 flex-none place-items-center rounded-full text-current transition-colors hover:bg-foreground/[0.07]";
  // Backward navigation is a drop-off CAUSE, and this is the ONE back
  // affordance in the funnel, so recording it here covers every screen.
  const noteBack = () => recordSteppedBack(window.location.pathname, to);
  if (to) {
    return (
      <Link to={to} aria-label={label} className={cls} onClick={noteBack}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        noteBack();
        onClick?.();
      }}
      aria-label={label}
      className={cn(cls, "cursor-pointer border-0 bg-transparent p-0")}
    >
      {inner}
    </button>
  );
}

export function Wordmark({ tone = "ink", to }: { tone?: "ink" | "paper"; to?: string }) {
  const cls = cn(
    "font-brand text-[20px] font-semibold tracking-[-0.02em]",
    tone === "ink" ? "text-ink" : "text-paper",
  );
  return to ? (
    <Link to={to} className={cn(cls, "hover:text-grape")}>
      cardmoji
    </Link>
  ) : (
    <span className={cls}>cardmoji</span>
  );
}

export function StepLabel({ children, tone = "ink" }: { children: ReactNode; tone?: "ink" | "paper" }) {
  return (
    <span
      className={cn(
        "text-[11px] font-semibold uppercase tracking-[0.1em]",
        tone === "ink" ? "text-slate-muted" : "text-paper/60",
      )}
    >
      {children}
    </span>
  );
}

export function FunnelHeader({
  backTo,
  backLabel,
  onBack,
  step,
  right,
  tone = "ink",
  className,
}: {
  backTo?: string;
  backLabel?: string;
  onBack?: () => void;
  step?: ReactNode;
  right?: ReactNode;
  tone?: "ink" | "paper";
  className?: string;
}) {
  return (
    <div className={cn("mb-[18px] flex items-center gap-2.5", className)}>
      {(backTo || onBack) && <BackChevron to={backTo} onClick={onBack} label={backLabel || "Back"} />}
      <Wordmark tone={tone} />
      <span className="flex-1" />
      {step ? <StepLabel tone={tone}>{step}</StepLabel> : null}
      {right}
    </div>
  );
}

/* --------------------------------------------------------------- typography */

export function Display({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h1
      className={cn(
        "font-brand text-[32px] font-semibold leading-[1.03] tracking-[-0.03em] text-balance md:text-[clamp(32px,4.6vw,54px)]",
        className,
      )}
    >
      {children}
    </h1>
  );
}

export function Lede({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("text-[16px] leading-[1.45] text-foreground/65 text-pretty md:text-[clamp(16px,1.5vw,19px)]", className)}>
      {children}
    </p>
  );
}

export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h2 className={cn("font-brand text-[20px] font-semibold tracking-[-0.01em]", className)}>{children}</h2>;
}

/* ------------------------------------------------------------------ surface */

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-[14px] border border-foreground/10 bg-white p-[15px]", className)}>{children}</div>
  );
}

export function Pill({
  active,
  children,
  ...rest
}: { active?: boolean; children: ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        "min-h-11 flex-none rounded-full border px-[18px] py-3 text-[14px] font-semibold transition-colors",
        active
          ? "border-ink bg-ink text-paper"
          : "border-foreground/[0.18] bg-white text-ink hover:border-ink",
      )}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ actions */

export const PrimaryButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(function PrimaryButton({
  children,
  className,
  ...rest
}, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        "min-h-[52px] w-full rounded-full border-0 bg-grape px-[30px] py-[15px] text-[17px] font-semibold text-white transition-colors hover:bg-tangerine disabled:pointer-events-none disabled:opacity-[0.35]",
        className,
      )}
    >
      {children}
    </button>
  );
});

export const GhostButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(function GhostButton({
  children,
  className,
  ...rest
}, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        "min-h-[52px] w-full rounded-full border border-foreground/[0.18] bg-white px-[26px] py-[15px] text-[16px] font-semibold text-ink transition-colors hover:border-ink disabled:pointer-events-none disabled:opacity-40",
        className,
      )}
    >
      {children}
    </button>
  );
});

/**
 * G3 · the ONE shape for a secondary action in the funnel: a blue sentence
 * link, visually subordinate to the pinned primary CTA. Never a second button.
 */
export const SentenceLink = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(function SentenceLink({
  children,
  className,
  ...rest
}, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        "min-h-[44px] bg-transparent p-0 text-[15px] font-semibold text-azure underline underline-offset-[3px] transition-colors hover:text-tangerine disabled:pointer-events-none disabled:opacity-40",
        className,
      )}
    >
      {children}
    </button>
  );
});


/** Sticky bottom action bar (matches the design's paper/blur/hairline treatment). */
export function StickyBar({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "children">) {
  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-20 border-t border-foreground/10 bg-paper/[0.94] backdrop-blur-[10px]",
        className,
      )}
      {...rest}
    >
      <div className="mx-auto flex w-full max-w-[720px] flex-wrap items-center gap-x-4 gap-y-2.5 px-4 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3 md:px-8">
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- fields */

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-2 block text-[14px] font-semibold">{label}</span>
      {children}
      {hint && !error ? <span className="mt-1.5 block text-[13px] text-foreground/55">{hint}</span> : null}
      {error ? <span className="mt-1.5 block text-[13px] font-medium text-[#EC1314]">{error}</span> : null}
    </label>
  );
}

export const inputClass =
  "h-[52px] w-full rounded-[12px] border border-foreground/[0.14] bg-white px-4 text-[16px] text-ink outline-none transition-colors placeholder:text-foreground/[0.32] focus:border-grape";

/* -------------------------------------------------------------------- icons */

export function TickIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={cn("h-5 w-5 flex-none", className)}>
      <circle cx="10" cy="10" r="10" fill="#248F59" />
      <path d="M5.6 10.4l2.8 2.8 6-6.4" fill="none" stroke="#FFFFFF" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CrossIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={cn("h-5 w-5 flex-none", className)}>
      <circle cx="10" cy="10" r="10" fill="#EC1314" />
      <path d="M6.6 6.6l6.8 6.8M13.4 6.6l-6.8 6.8" fill="none" stroke="#FFFFFF" strokeWidth="2.1" strokeLinecap="round" />
    </svg>
  );
}

export function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={cn("h-5 w-5 flex-none", className)}>
      <path d="M10 1.6l6.6 2.6v5.2c0 4-2.8 7.4-6.6 8.8-3.8-1.4-6.6-4.8-6.6-8.8V4.2z" fill="none" stroke="#6D7C93" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M6.9 10.2l2.2 2.2 4.2-4.6" fill="none" stroke="#6D7C93" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function GoodRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-[14px] border border-foreground/[0.09] bg-white p-[14px]">
      <TickIcon className="mt-px" />
      <span className="text-[15px] leading-[1.45]">{children}</span>
    </div>
  );
}

export function BadRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 px-[14px] py-[13px]">
      <CrossIcon className="mt-px" />
      <span className="text-[15px] leading-[1.45]">{children}</span>
    </div>
  );
}

/* --------------------------------------------------------------- card faces */

/**
 * Card face in the canonical 260/412 portrait ratio.
 * Art assets land later — until then the colourway plus a neutral placeholder
 * stand in. NO scheme mark and NO brand text is rendered (L1 gate).
 *
 * There is deliberately no `brandName` prop. It used to print the issuer brand
 * as TEXT bottom-right (where the network mark sits on the physical card) and
 * fell back to the programme name — so the consumer funnel printed the literal
 * word "CARDMOJI" on every preview card, while the confirmed/shared card (which
 * never passed the prop) showed nothing. Preview must equal final: one render
 * path, no per-screen brand branch. If a real brand/network MARK is ever added,
 * it belongs in this component unconditionally, driven by the designed-card
 * model — never as a per-screen prop and never as placeholder text.
 */
export function CardFace({
  color,
  art,
  alt,
  className,
  children,
  style,
  artStyle,
  artClassName,
  chip = true,
  ...rest
}: {
  color?: string;
  art?: string | null;
  alt?: string;
  className?: string;
  children?: ReactNode;
  style?: React.CSSProperties;
  /**
   * Framing applied to the AVATAR ONLY (zoom/reposition). It must never be put
   * on the card element itself: the card is the fixed physical object, the
   * avatar moves inside it.
   */
  artStyle?: React.CSSProperties;
  /** Fit override for the art. Cards letterbox (contain); round avatars fill. */
  artClassName?: string;
  /**
   * The EMV chip belongs to a CARD. Surfaces that reuse this art-on-colour
   * primitive for something that is NOT a card (the round profile avatar) pass
   * false — otherwise a gold chip appears inside the person's avatar.
   */
  chip?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "color" | "style" | "className" | "children">) {
  return (
    <div
      className={cn("relative aspect-[260/412] w-full overflow-hidden rounded-[14px] shadow-[0_6px_18px_rgba(28,32,48,0.16)]", className)}
      style={{ background: color || "hsl(var(--ink))", ...style }}
      {...rest}
    >
      {art ? (
        <img
          src={art}
          alt={alt || ""}
          /*
            NO loading="lazy" and crossOrigin="anonymous" are BOTH load-bearing for
            SHARING, not for the on-screen render. The share compositor rasterises
            this exact node while it sits off-screen at left:-9999px; a lazy image in
            an off-screen container never decodes (complete:false, naturalWidth:0),
            so the only copy is the one html-to-image re-fetches at capture time —
            and any failed fetch silently produces an avatar-less card. crossOrigin
            keeps the storage response usable in the canvas instead of tainting it.
          */
          crossOrigin="anonymous"

          /*
            draggable=false is LOAD-BEARING: a native image drag hijacks the
            pointer sequence (it starts an HTML5 drag and the browser releases
            pointer capture), which is why drag-to-reposition "did nothing" on
            desktop — the gesture became a ghost-image drag instead of a move.
          */
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          className={cn("absolute inset-0 h-full w-full object-contain", artClassName)}
          style={artStyle}
        />
      ) : (
        /*
          F1 · NO label inside the card. The style name is already rendered under
          the tile; printing it here again produced the "Persian cat / PERSIAN
          CAT" repeat and truncated on narrow tiles. The empty card keeps the
          accessible name via `aria-label` instead.
        */
        <div className="absolute inset-0" aria-label={alt} role={alt ? "img" : undefined} />
      )}

      {/* EMV chip — cards only. */}
      {chip ? (
        <div
          className="absolute left-[7%] top-[7%] aspect-[4/3] w-[15%] rounded-[3px]"
          style={{ background: "linear-gradient(150deg, #F0D592, #D9B46A 42%, #B8913F)" }}
        />
      ) : null}
      {children}
    </div>
  );
}

